import {
  Menu,
  MenuButton,
  MenuList,
  ButtonIcon,
  MenuItem,
  Badge
} from '@morressier/alembic';
import { Box, Text, Flex, Tooltip } from '@chakra-ui/react';
import { IPeerReviewListItem } from '@publishing-engine/domain/views';
import {
  recommendationBadges,
  reviewStatusBadges
} from 'src/components/Review/Badges';
import { formatDeadlineDate } from 'src/utils/date';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/pro-regular-svg-icons';
import { faBars } from '@fortawesome/pro-solid-svg-icons';
import { calculateDaysToDeadline } from 'src/utils/deadlines';
import { IReviewer } from '@publishing-engine/domain/models';
import { URGENCY_THRESHOLD } from '@publishing-engine/domain/fixtures/PeerReview';
import { useSubmissionReviewsData } from 'src/contexts/SubmissionReviewsDataProvider';

export const renderReviewStatusColumn = (review: IPeerReviewListItem) => {
  const deadlineExtensionRequested =
    review?.workflow?.deadlineExtensions?.status === 'requested';
  const currentState = deadlineExtensionRequested
    ? 'extensionRequested'
    : review?.workflow?.currentState;

  const declinedReviewReason =
    review?.caseData?.additionalInformation?.declineReason;
  const recommendedReviewers =
    review?.caseData?.additionalInformation?.recommendedReviewers;

  const hasAdditionalReviewInfo =
    declinedReviewReason ||
    (recommendedReviewers && recommendedReviewers.length > 0);

  const currentStateIncludingDeclinedWithReason =
    declinedReviewReason && currentState === 'declined'
      ? 'declinedWithReason'
      : currentState;

  const statusBadge = currentState
    ? reviewStatusBadges[currentStateIncludingDeclinedWithReason]
    : reviewStatusBadges.invited;

  if (currentState === 'declined' && hasAdditionalReviewInfo) {
    return (
      <Box width="100%" overflow="hidden">
        <Flex alignItems="center" gap={2}>
          <Box minWidth={0} overflow="hidden">
            {statusBadge}
          </Box>

          <Tooltip
            shouldWrapChildren
            hasArrow
            label="This Reviewer has sent information about this decision."
            placement="top"
          >
            <Box flexShrink={0}>
              <Badge size="context" variant="caution" alignItems="center">
                <FontAwesomeIcon icon={faInfoCircle} />
              </Badge>
            </Box>
          </Tooltip>
        </Flex>
      </Box>
    );
  }

  return (
    <Box width="100%" overflow="hidden">
      {statusBadge}
    </Box>
  );
};

export const renderDeadlineColumn = (review: IPeerReviewListItem) => {
  const { currentState } = review.workflow;

  if (
    ['awaitingRevisionReviewRequest', 'submittedRevisionAssessment'].includes(
      currentState
    )
  ) {
    return 'N/A';
  }

  const deadlineDate = review.workflow.currentDeadline
    ? new Date(review.workflow.currentDeadline)
    : null;
  const daysToDeadline = deadlineDate
    ? calculateDaysToDeadline(deadlineDate)
    : null;

  const isUrgent =
    typeof daysToDeadline === 'number' && daysToDeadline <= URGENCY_THRESHOLD;

  let deadlineText = '';
  if (daysToDeadline && daysToDeadline >= 0) {
    deadlineText = `${daysToDeadline} days to submit their Review`;
  } else {
    deadlineText = daysToDeadline
      ? `${Math.abs(daysToDeadline)} days overdue`
      : '';
  }

  return (
    <>
      <Text
        textStyle="headingItem"
        color={isUrgent ? 'actionSecondaryCautionContent' : ''}
      >
        {deadlineDate ? formatDeadlineDate(deadlineDate) : null}
      </Text>
      <Text textStyle="textContext">{deadlineText}</Text>
    </>
  );
};

export const renderRecommendationColumn = (review: IPeerReviewListItem) => {
  const { hasSubmittedReviewInCurrentRound } = useSubmissionReviewsData();

  if (!hasSubmittedReviewInCurrentRound(review.id)) {
    return recommendationBadges.awaitingReview;
  }

  if (
    review.caseData.firstAssessment?.recommendation &&
    !review.caseData.revisionAssessment
  ) {
    return recommendationBadges[review.caseData.firstAssessment.recommendation];
  }

  if (!review.caseData.revisionAssessment?.recommendation) {
    return 'N/A';
  }

  return recommendationBadges[
    review.caseData.revisionAssessment.recommendation
  ];
};

const renderMenuItem = (
  label: string,
  onClick: () => void,
  textColor?: string,
  disabled?: boolean,
  tooltip?: string
) => {
  return (
    <Box onClick={event => event.stopPropagation()}>
      {tooltip && disabled ? (
        <Tooltip label={tooltip} hasArrow placement="top" shouldWrapChildren>
          <MenuItem disabled={disabled} ariaLabel={label} onClick={onClick}>
            <Text textColor={textColor}>{label}</Text>
          </MenuItem>
        </Tooltip>
      ) : (
        <MenuItem disabled={disabled} ariaLabel={label} onClick={onClick}>
          <Text textColor={textColor}>{label}</Text>
        </MenuItem>
      )}
    </Box>
  );
};

export const renderActionsColumn = (
  review: IPeerReviewListItem,
  onSelectReviewId: (reviewId: string) => void,

  onSelectedAuthor: (author: IReviewer | null) => void,
  onRateReview: (reviewId: string) => void,
  isUserDeactivated?: boolean
) => {
  const { hasSubmittedReviewInCurrentRound } = useSubmissionReviewsData();

  const isDisabled = isUserDeactivated ?? false;
  const disabledTooltip = isUserDeactivated ? 'User deactivated' : undefined;

  const renderItem = (
    label: string,
    onClick: () => void,
    textColor?: string
  ) => renderMenuItem(label, onClick, textColor, isDisabled, disabledTooltip);

  return (
    <>
      {/* These divs and styles are needed because the Menu was rendering in the topLeft of the screen */}
      <Box position={'relative'}>
        <Menu ariaLabel="Actions">
          <MenuButton
            as={ButtonIcon}
            onClick={event => {
              event.stopPropagation();
            }}
            icon={<FontAwesomeIcon icon={faBars} />}
            color="transparent"
          />
          <MenuList style={{ position: 'absolute', zIndex: 1000, top: '48px' }}>
            {hasSubmittedReviewInCurrentRound(review.id) &&
              renderItem('View Review', () => {
                onSelectReviewId(review.id);
              })}

            {renderItem('View Reviewer Profile', () =>
              onSelectedAuthor(review.reviewer)
            )}
            {review.canRateReview &&
              renderItem('Rate Review', () => {
                onRateReview(review.id);
              })}
          </MenuList>
        </Menu>
      </Box>
    </>
  );
};
